import Cookies from 'js-cookie'
import {v4 as uuidv4} from 'uuid'

import compactObject from '@/utils/compact-object'
import {getConversionValues} from '@/utils/conversion-values'
import {cookiesMap} from '@helios/shared/config/cookies'
import type {CountryCode as SupportedCountryCode} from '@helios/shared/locale/locales'
import type CompanySize from '@helios/shared/types/company-size'
import {track, identify, TrackingEvent} from '@helios/shared/utils/analytics'

import type {State as BigformFormState} from './bigform'
import type {State as DemoFormState} from './demo-form'
import type {State as EventBigformFormState} from './event/event-bigform'
import type {State as HubspotGlobalFormState} from './global/hubspot-global-form'
import type {State as PartnerBigformFormState} from './partner/partner-bigform'

export const OTHER = 'Other' as const

export enum FunnelStage {
    BigformReached = 'bigform_reached',
    BigformSubmitted = 'bigform_submitted',
    PleoDemoReached = 'pleodemo_reached',
    PleoDemoBooked = 'pleodemo_booked',
}

interface BigformReachedArgs {
    country: SupportedCountryCode
    trackQualifyingLeads?: boolean
}

export const bigformReached = (data: BigformReachedArgs) => {
    const params = new URLSearchParams(window.location?.search)
    const email = params.get('email')

    const {trackQualifyingLeads} = data

    email && identify({email})
    track(TrackingEvent.BigformActioned, {action: 'started'})
    track(TrackingEvent.BigformViewed)

    if (trackQualifyingLeads && !email?.match(/.*gmail.*/i)) {
        track(TrackingEvent.SignupActioned, {
            action: 'started',
            value: 1,
            fbc: Cookies.get(cookiesMap.metaClickId) || '',
            fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
            ttclid: Cookies.get(cookiesMap.tikTokPixelId) || '',
        })
    }
}

type BigformSubmittedArgs =
    | BigformFormState
    | DemoFormState
    | HubspotGlobalFormState
    | PartnerBigformFormState
    | EventBigformFormState

export const bigformSubmitted = (data: BigformSubmittedArgs) => {
    const {email, country} = data
    const traits = compactObject({
        email,
        phone: 'phoneNumber' in data && data.phoneNumber,
        name: 'userFullName' in data && data.userFullName,
        firstName: 'firstName' in data && data.firstName,
        lastName: 'lastName' in data && data.lastName,
        marketingEmails: 'marketingEmails' in data && data.marketingEmails,
        form: 'DirectBigForm',
        external_id: window.location.pathname.includes('/demo') && uuidv4(),
        company: {
            name: 'companyName' in data && data.companyName,
            employee_count:
                'companySize' in data &&
                Number(data.companySize.split('-').pop()?.replace('+', '')),
        },
    })

    identify(traits)
    track(TrackingEvent.BigformActioned, {action: 'completed', ...data})
    track(TrackingEvent.BigformSubmitted, data)

    if (!country) {
        return
    }
}

// Track interaction with fields (e.g. typed character in text input, or selection in select)
// We only track interaction with each element once per session
const fieldsTracked: Record<string, boolean> = {}
export const bigformInteracted = (field: string) => {
    if (fieldsTracked[field]) {
        return
    }
    track(TrackingEvent.BigformActioned, {action: 'interacted', field})
    fieldsTracked[field] = true
}

interface PleoDemoReachedArgs {
    country?: string | null
    companySize?: string | null
}

export const pleoDemoReached = (data: PleoDemoReachedArgs) => {
    const {country} = data

    track(TrackingEvent.DemoBookingActioned, {action: 'started'})
    track(TrackingEvent.DemoBookingViewed)

    if (!country) {
        return
    }
}

interface PleoDemoBookedArgs {
    country?: string | null
    companySize?: string | null
}

export const pleoDemoBooked = (data: PleoDemoBookedArgs) => {
    const {country} = data

    track(TrackingEvent.DemoBookingActioned, {action: 'completed'})
    track(TrackingEvent.DemoBookingSubmitted, {
        fbc: Cookies.get(cookiesMap.metaClickId) || '',
        fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
    })

    if (!country) {
        return
    }
}

export const demoBookingTimeSelected = () => {
    track(TrackingEvent.DemoBookingActioned, {action: 'clicked'})
}

interface GoogleAdsConversionArgs {
    country?: SupportedCountryCode
    companySize?: CompanySize
}

export const googleAdsConversion = ({country, companySize}: GoogleAdsConversionArgs) => {
    if (country && companySize) {
        const conversionValues = getConversionValues(companySize, country)

        if (conversionValues) {
            track(TrackingEvent.GoogleAdsOptimisationSignal, conversionValues)
        }
    }
}
